import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import {
  Car,
  MapPin,
  Calendar,
  Clock,
  Users,
  Package,
  Plus,
  ArrowLeft,
  Loader2,
  X
} from 'lucide-react';

const AMENITIES = [
  { id: 'ac', label: 'AC' },
  { id: 'music', label: 'Music' },
  { id: 'wifi', label: 'WiFi' },
  { id: 'luggage_space', label: 'Luggage Space' },
  { id: 'pet_friendly', label: 'Pet Friendly' },
  { id: 'smoking_allowed', label: 'Smoking Allowed' }
];

export default function OfferRide() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    ride_type: 'both',
    origin: '',
    destination: '',
    departure_date: '',
    departure_time: '',
    vehicle_type: 'car',
    vehicle_name: '',
    vehicle_number: '',
    available_seats: 3,
    price_per_seat: 0,
    cargo_space_available: false,
    cargo_capacity_kg: 0,
    price_per_kg: 0,
    amenities: [],
    notes: '',
    stops: []
  });

  const [newStop, setNewStop] = useState('');

  const updateForm = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const toggleAmenity = (amenityId) => {
    setForm(f => ({
      ...f,
      amenities: f.amenities.includes(amenityId)
        ? f.amenities.filter(a => a !== amenityId)
        : [...f.amenities, amenityId]
    }));
  };

  const addStop = () => {
    if (newStop.trim()) {
      setForm(f => ({ ...f, stops: [...f.stops, newStop.trim()] }));
      setNewStop('');
    }
  };

  const removeStop = (index) => {
    setForm(f => ({ ...f, stops: f.stops.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.origin || !form.destination) {
      toast.error('Please enter origin and destination');
      return;
    }

    if (!form.departure_date || !form.departure_time) {
      toast.error('Please select date and time');
      return;
    }

    if (form.ride_type !== 'cargo' && form.available_seats < 1) {
      toast.error('Please specify available seats');
      return;
    }

    if (form.ride_type !== 'cargo' && form.price_per_seat < 1) {
      toast.error('Please set price per seat');
      return;
    }

    if (form.cargo_space_available && form.cargo_capacity_kg < 1) {
      toast.error('Please specify cargo capacity');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/carpool/listings', form);
      toast.success('Ride created successfully!');
      navigate(`/carpool/${response.data.listing_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ride');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-accent text-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/carpool" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Carpool
          </Link>
          <h1 className="text-3xl font-black">Offer a Ride</h1>
          <p className="opacity-90 mt-2">
            Share your journey and earn money while helping others travel
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Ride Type */}
          <div className="border border-border p-6">
            <h2 className="text-lg font-bold mb-4">What are you offering?</h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'passenger', label: 'Passengers Only', icon: Users },
                { value: 'cargo', label: 'Cargo Only', icon: Package },
                { value: 'both', label: 'Both', icon: Car }
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => updateForm('ride_type', value)}
                  className={`p-4 border text-center transition-colors ${
                    form.ride_type === value
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <Icon className={`w-6 h-6 mx-auto mb-2 ${form.ride_type === value ? 'text-accent' : 'text-muted-foreground'}`} />
                  <span className={`text-sm ${form.ride_type === value ? 'text-accent font-medium' : ''}`}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Route */}
          <div className="border border-border p-6">
            <h2 className="text-lg font-bold mb-4">Route Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Origin *</Label>
                <div className="relative mt-2">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={form.origin}
                    onChange={(e) => updateForm('origin', e.target.value)}
                    placeholder="Starting city/location"
                    className="pl-10 rounded-none"
                    required
                    data-testid="offer-origin-input"
                  />
                </div>
              </div>
              <div>
                <Label>Destination *</Label>
                <div className="relative mt-2">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={form.destination}
                    onChange={(e) => updateForm('destination', e.target.value)}
                    placeholder="Ending city/location"
                    className="pl-10 rounded-none"
                    required
                    data-testid="offer-destination-input"
                  />
                </div>
              </div>
            </div>

            {/* Stops */}
            <div className="mt-4">
              <Label>Intermediate Stops (optional)</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newStop}
                  onChange={(e) => setNewStop(e.target.value)}
                  placeholder="Add a stop along the way"
                  className="rounded-none"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStop())}
                />
                <Button type="button" onClick={addStop} variant="outline" className="rounded-none">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.stops.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.stops.map((stop, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-muted text-sm"
                    >
                      {stop}
                      <button type="button" onClick={() => removeStop(index)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="border border-border p-6">
            <h2 className="text-lg font-bold mb-4">When?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Departure Date *</Label>
                <div className="relative mt-2">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={form.departure_date}
                    onChange={(e) => updateForm('departure_date', e.target.value)}
                    min={today}
                    className="pl-10 rounded-none"
                    required
                    data-testid="offer-date-input"
                  />
                </div>
              </div>
              <div>
                <Label>Departure Time *</Label>
                <div className="relative mt-2">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="time"
                    value={form.departure_time}
                    onChange={(e) => updateForm('departure_time', e.target.value)}
                    className="pl-10 rounded-none"
                    required
                    data-testid="offer-time-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle */}
          <div className="border border-border p-6">
            <h2 className="text-lg font-bold mb-4">Vehicle Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Vehicle Type *</Label>
                <select
                  value={form.vehicle_type}
                  onChange={(e) => updateForm('vehicle_type', e.target.value)}
                  className="w-full mt-2 h-10 px-3 border border-input bg-background rounded-none"
                  data-testid="offer-vehicle-type"
                >
                  <option value="car">Car</option>
                  <option value="suv">SUV</option>
                  <option value="van">Van</option>
                  <option value="bike">Bike</option>
                </select>
              </div>
              <div>
                <Label>Vehicle Name</Label>
                <Input
                  value={form.vehicle_name}
                  onChange={(e) => updateForm('vehicle_name', e.target.value)}
                  placeholder="e.g., Honda City"
                  className="rounded-none mt-2"
                />
              </div>
              <div>
                <Label>Vehicle Number</Label>
                <Input
                  value={form.vehicle_number}
                  onChange={(e) => updateForm('vehicle_number', e.target.value)}
                  placeholder="e.g., MH 01 AB 1234"
                  className="rounded-none mt-2"
                />
              </div>
            </div>

            {/* Amenities */}
            <div className="mt-4">
              <Label>Amenities</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {AMENITIES.map((amenity) => (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() => toggleAmenity(amenity.id)}
                    className={`px-3 py-1.5 border text-sm transition-colors ${
                      form.amenities.includes(amenity.id)
                        ? 'border-accent bg-accent text-white'
                        : 'border-border hover:border-accent'
                    }`}
                  >
                    {amenity.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Passenger Seats */}
          {form.ride_type !== 'cargo' && (
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4">Passenger Seats</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Available Seats *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={form.available_seats}
                    onChange={(e) => updateForm('available_seats', Number(e.target.value))}
                    className="rounded-none mt-2"
                    required
                    data-testid="offer-seats-input"
                  />
                </div>
                <div>
                  <Label>Price per Seat (₹) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.price_per_seat}
                    onChange={(e) => updateForm('price_per_seat', Number(e.target.value))}
                    placeholder="e.g., 500"
                    className="rounded-none mt-2"
                    required
                    data-testid="offer-price-seat-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Cargo Space */}
          {form.ride_type !== 'passenger' && (
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4">Cargo Space</h2>
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="cargo_space"
                  checked={form.cargo_space_available}
                  onChange={(e) => updateForm('cargo_space_available', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="cargo_space" className="cursor-pointer">
                  I have cargo space available
                </Label>
              </div>
              
              {form.cargo_space_available && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Cargo Capacity (kg) *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.cargo_capacity_kg}
                      onChange={(e) => updateForm('cargo_capacity_kg', Number(e.target.value))}
                      placeholder="e.g., 50"
                      className="rounded-none mt-2"
                      data-testid="offer-cargo-capacity-input"
                    />
                  </div>
                  <div>
                    <Label>Price per kg (₹) *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.price_per_kg}
                      onChange={(e) => updateForm('price_per_kg', Number(e.target.value))}
                      placeholder="e.g., 10"
                      className="rounded-none mt-2"
                      data-testid="offer-price-kg-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="border border-border p-6">
            <h2 className="text-lg font-bold mb-4">Additional Information</h2>
            <Textarea
              value={form.notes}
              onChange={(e) => updateForm('notes', e.target.value)}
              placeholder="Any rules or additional information passengers should know..."
              className="rounded-none"
              rows={3}
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/carpool')}
              className="flex-1 rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-none bg-accent hover:bg-accent/90"
              data-testid="create-ride-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Car className="w-4 h-4 mr-2" />
                  Publish Ride
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
